import { useState, useCallback } from "react";

const STORAGE_KEY = "assetScheduleTurnTimeMinutes";
const DEFAULT_TURN_TIME = 30;

interface UseTurnTimeResult {
  /** Current turn time in minutes */
  turnTimeMinutes: number;
  /** Update turn time (clamped to >= 0, persisted to localStorage) */
  handleTurnTimeChange: (value: number) => void;
}

/**
 * Hook for managing turn time configuration across schedule views.
 * Turn time is the minimum buffer required between a resource's arrival
 * at a destination and its next departure.
 * 
 * State is persisted to localStorage and shared across all schedule pages.
 */
export function useTurnTime(defaultMinutes: number = DEFAULT_TURN_TIME): UseTurnTimeResult {
  const [turnTimeMinutes, setTurnTimeMinutes] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? parseInt(stored, 10) : defaultMinutes;
  });

  const handleTurnTimeChange = useCallback((value: number) => {
    const clamped = Math.max(0, value);
    setTurnTimeMinutes(clamped);
    localStorage.setItem(STORAGE_KEY, String(clamped));
  }, []);

  return { turnTimeMinutes, handleTurnTimeChange };
}
